
import { gToLb } from "@/utils/familyProfile";
import { GrowthChart } from "./GrowthChart";
import { createChartConfig } from "./chart/ChartConfig";

interface WeightChartProps { 
  weightData: any[];
  displayOriginalWeight: number | null;
  useMetricWeight: boolean;
  handleWeightUnitToggle: () => void;
  babyAgeInMonths: number;
}

// Average baby weight data (in grams) by month
// These are approximate values based on WHO growth charts
const averageWeightData = [
  { month: 0, value: 3400 },  // Birth
  { month: 1, value: 4500 },  // 1 month
  { month: 2, value: 5600 },  // 2 months
  { month: 3, value: 6400 },  // 3 months
  { month: 4, value: 7000 },  // 4 months
  { month: 6, value: 8000 },  // 6 months
  { month: 9, value: 9000 },  // 9 months
  { month: 12, value: 10000 }, // 12 months
  { month: 15, value: 10700 }, // 15 months
  { month: 18, value: 11200 }, // 18 months
  { month: 24, value: 12200 }, // 24 months (2 years)
  { month: 30, value: 13100 }, // 30 months
  { month: 36, value: 14000 }  // 36 months (3 years)
];

// Chart configuration
const chartConfig = createChartConfig(
  "weight",
  "Weight",
  {
    light: "#0ea5e9", // Sky Blue (original color)
    dark: "#7dd3fc"
  }
);

export function WeightChart({ 
  weightData, 
  displayOriginalWeight, 
  useMetricWeight, 
  handleWeightUnitToggle,
  babyAgeInMonths
}: WeightChartProps) {
  const weightUnitLabel = useMetricWeight ? "g" : "lb";
  
  // Function to get average weight for current age in months
  const getAverageWeightForAge = (ageMonths: number) => {
    // Find the closest data point
    let closestPoint = averageWeightData[0];
    
    for (const point of averageWeightData) {
      if (Math.abs(point.month - ageMonths) < Math.abs(closestPoint.month - ageMonths)) {
        closestPoint = point;
      }
    }
    
    return useMetricWeight ? closestPoint.value : gToLb(closestPoint.value);
  };

  // Convert average data to current unit
  const convertedAverageData = averageWeightData.map(point => ({
    month: point.month,
    value: useMetricWeight ? point.value : gToLb(point.value)
  }));

  return (
    <GrowthChart
      title="Weight Growth"
      description="Track your baby's weight progress over time"
      chartData={weightData}
      displayOriginalValue={displayOriginalWeight}
      useMetricUnit={useMetricWeight}
      handleUnitToggle={handleWeightUnitToggle}
      unitLabel={weightUnitLabel}
      averageData={convertedAverageData}
      getAverageValueForAge={getAverageWeightForAge}
      valueKey="displayWeight"
      chartConfig={chartConfig}
      babyAgeInMonths={babyAgeInMonths}
    />
  );
}

